import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './components/ui/avatar';
import {
  Search, Dumbbell, Apple, Users, X, Loader2,
  Star, ChevronRight, SlidersHorizontal, ChevronDown, ChevronUp, Flame, CheckCircle
} from 'lucide-react';
import { API } from './config';
import { WorkoutPlansTab } from './WorkoutPlanComponents';
import { MealPlansTab } from './MealPlanComponents';

const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${API.replace('/api', '')}${url}`;
  return url;
};

const INITIAL_SHOW = 6;

const TYPE_TABS = [
  { id: 'all', label: 'All', icon: null },
  { id: 'workouts', label: 'Workouts', icon: Dumbbell },
  { id: 'meals', label: 'Meals', icon: Apple },
  { id: 'creators', label: 'Creators', icon: Users },
  { id: 'challenges', label: 'Challenges', icon: Flame },
];

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest' },
  { id: 'popular', label: 'Popular' },
];

// ── Creator Card ─────────────────────────────────────────────────────────
const CreatorCard = ({ creator }) => {
  const navigate = useNavigate();
  return (
    <Card
      className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-purple-100 cursor-pointer"
      onClick={() => navigate(`/profile/${creator.id}`)}
      data-testid={`discover-creator-${creator.id}`}
    >
      <div className="h-20 rounded-t-lg bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 relative overflow-hidden">
        {creator.cover_photo && (
          <img src={resolveMediaUrl(creator.cover_photo)} alt="" className="w-full h-full object-cover absolute inset-0" />
        )}
        <div className="absolute inset-0 bg-black/10" />
      </div>
      <CardContent className="pt-0 -mt-8 relative">
        <div className="flex items-end gap-3 mb-3">
          <Avatar className="h-14 w-14 ring-3 ring-white shadow-md">
            <AvatarImage src={resolveMediaUrl(creator.profile_picture)} />
            <AvatarFallback className="bg-purple-100 text-purple-700 font-bold text-lg">
              {creator.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 pb-1">
            <div className="flex items-center gap-1.5">
              <h4 className="font-semibold text-sm text-gray-900 truncate">{creator.name}</h4>
              <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 shrink-0" />
            </div>
          </div>
        </div>
        {creator.bio && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{creator.bio}</p>}
        {(creator.content_niches || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {creator.content_niches.slice(0, 4).map(n => (
              <Badge key={n} variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{n.replace(/_/g, ' ')}</Badge>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            {(creator.follower_count || 0) > 0 && <span>{creator.follower_count} followers</span>}
            {(creator.workout_count || 0) > 0 && (
              <span className="flex items-center gap-0.5"><Dumbbell className="h-3 w-3" />{creator.workout_count}</span>
            )}
            {(creator.meal_count || 0) > 0 && (
              <span className="flex items-center gap-0.5"><Apple className="h-3 w-3" />{creator.meal_count}</span>
            )}
            {(creator.post_count || 0) > 0 && <span>{creator.post_count} posts</span>}
          </div>
          <div className="flex items-center gap-1 text-xs">
            {creator.subscription_price > 0 ? (
              <span className="font-semibold text-purple-600">${creator.subscription_price}/mo</span>
            ) : (
              <span className="font-medium text-green-600">Free</span>
            )}
            <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-purple-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Section with "See more / Show less" ──────────────────────────────────
const DiscoverSection = ({ icon: Icon, iconColor, label, count, items, renderItems, testIdPrefix }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = items.length > INITIAL_SHOW;
  const visible = expanded ? items : items.slice(0, INITIAL_SHOW);

  return (
    <div data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</h3>
        <Badge variant="outline" className="text-[10px] ml-1 dark:border-gray-500 dark:text-gray-300">{count}</Badge>
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            data-testid={`${testIdPrefix}-see-more`}
          >
            {expanded ? (
              <><ChevronUp className="h-3.5 w-3.5" />Show less</>
            ) : (
              <><ChevronDown className="h-3.5 w-3.5" />See more ({items.length - INITIAL_SHOW})</>
            )}
          </button>
        )}
      </div>
      {renderItems(visible)}
    </div>
  );
};

// ── Main Discover Component ──────────────────────────────────────────────
// Challenge Discover Card
const ChallengeDiscoverCard = ({ challenge }) => {
  const navigate = useNavigate();
  const audienceLabel = { everyone: 'Public', followers: 'Followers', subscribers: 'Subscribers' };
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/profile/${challenge.creator_id}`)} data-testid={`discover-challenge-${challenge.id}`}>
      {challenge.cover_image && (
        <img src={resolveMediaUrl(challenge.cover_image)} alt="" className="w-full h-28 object-cover" />
      )}
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Flame className="h-3.5 w-3.5 text-amber-500" />
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{challenge.challenge_type || 'General'}</Badge>
        </div>
        <h3 className="text-sm font-bold text-gray-900 line-clamp-1">{challenge.title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">by {challenge.creator_name}</p>
        {challenge.description && <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{challenge.description}</p>}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400">
          <span>{audienceLabel[challenge.audience] || 'Public'}</span>
          <span>·</span>
          <span>{challenge.participants_count || 0} joined</span>
          {challenge.start_date && <><span>·</span><span>Starts {challenge.start_date}</span></>}
        </div>
      </CardContent>
    </Card>
  );
};

export const DiscoverTab = () => {
  const [query, setQuery] = useState('');
  const [activeNiche, setActiveNiche] = useState(null);
  const [activeType, setActiveType] = useState('all');
  const [activeSort, setActiveSort] = useState('newest');
  const [niches, setNiches] = useState([]);
  const [results, setResults] = useState({ workouts: [], meals: [], creators: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/fitness-niches`).then(r => setNiches(r.data)).catch(() => {});
  }, []);

  const fetchResults = useCallback(async (q, niche, type, sort) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (niche) params.set('niche', niche);
      if (type && type !== 'all') params.set('type', type);
      params.set('sort', sort);
      params.set('limit', '30');
      const res = await axios.get(`${API}/discover?${params.toString()}`);
      setResults(res.data);
    } catch {
      setResults({ workouts: [], meals: [], creators: [], total: 0 });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchResults('', null, 'all', 'newest'); }, [fetchResults]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchResults(val, activeNiche, activeType, activeSort);
    }, 350);
  };

  const handleNicheClick = (nicheId) => {
    const next = activeNiche === nicheId ? null : nicheId;
    setActiveNiche(next);
    fetchResults(query, next, activeType, activeSort);
  };

  const handleTypeChange = (typeId) => {
    setActiveType(typeId);
    fetchResults(query, activeNiche, typeId, activeSort);
  };

  const handleSortChange = (sortId) => {
    setActiveSort(sortId);
    fetchResults(query, activeNiche, activeType, sortId);
  };

  const clearFilters = () => {
    setQuery('');
    setActiveNiche(null);
    setActiveType('all');
    setActiveSort('newest');
    fetchResults('', null, 'all', 'newest');
  };

  const hasFilters = query || activeNiche || activeType !== 'all' || activeSort !== 'newest';
  const totalResults = results.total || 0;
  const showWorkouts = activeType === 'all' || activeType === 'workouts';
  const showMeals = activeType === 'all' || activeType === 'meals';
  const showCreators = activeType === 'all' || activeType === 'creators';
  const showChallenges = activeType === 'all' || activeType === 'challenges';

  return (
    <div className="space-y-5" data-testid="discover-tab">
      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search workouts, meals, creators..."
            value={query}
            onChange={handleQueryChange}
            className="pl-10 h-10 text-sm bg-white"
            data-testid="discover-search-input"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); fetchResults('', activeNiche, activeType, activeSort); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className={`h-10 px-3 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-500/20 dark:border-blue-500/40 dark:text-blue-400' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="discover-filter-toggle"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </div>

      {/* Type tabs + sort */}
      <div className="flex items-center gap-1.5">
        {TYPE_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => handleTypeChange(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors
              ${activeType === t.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            data-testid={`discover-type-${t.id}`}
          >
            {t.icon && <t.icon className="h-3.5 w-3.5" />}
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {SORT_OPTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => handleSortChange(s.id)}
              className={`px-2.5 py-1 rounded text-xs transition-colors
                ${activeSort === s.id
                  ? 'bg-gray-900 dark:bg-gray-200 text-white dark:text-gray-900'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              data-testid={`discover-sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Niche chips */}
      {showFilters && (
        <div className="flex flex-wrap gap-1.5 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700" data-testid="discover-niche-chips">
          {niches.map(n => (
            <button
              key={n.id}
              onClick={() => handleNicheClick(n.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all border
                ${activeNiche === n.id
                  ? 'text-white shadow-sm'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-gray-300'}`}
              style={activeNiche === n.id ? { backgroundColor: n.color, borderColor: n.color } : {}}
              data-testid={`discover-niche-${n.id}`}
            >
              {n.label}
            </button>
          ))}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-2.5 py-1 rounded-full text-xs font-medium text-red-500 border border-red-200 hover:bg-red-50"
              data-testid="discover-clear-filters"
            >
              <X className="h-3 w-3 inline mr-1" />Clear All
            </button>
          )}
        </div>
      )}

      {/* Active filter indicator */}
      {activeNiche && !showFilters && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Filtered by:</span>
          <button
            onClick={() => handleNicheClick(activeNiche)}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: niches.find(n => n.id === activeNiche)?.color || '#6B7280' }}
          >
            {niches.find(n => n.id === activeNiche)?.label}
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Searching...</span>
        </div>
      ) : totalResults === 0 ? (
        <div className="text-center py-16" data-testid="discover-empty">
          <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Search className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">No results found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {query ? `Nothing matched "${query}"` : 'Try adjusting your filters'}
          </p>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>Clear Filters</Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          <p className="text-xs text-gray-400 dark:text-gray-500">{totalResults} result{totalResults !== 1 ? 's' : ''}</p>

          {/* Workouts — limited to 6 initially, "See more" to expand */}
          {showWorkouts && results.workouts.length > 0 && (
            <DiscoverSection
              icon={Dumbbell}
              iconColor="text-blue-600"
              label="Workout Plans"
              count={results.workouts.length}
              items={results.workouts}
              testIdPrefix="discover-workouts"
              renderItems={(visible) => <WorkoutPlansTab workoutPlans={visible} />}
            />
          )}

          {/* Meals — limited to 6 initially */}
          {showMeals && results.meals.length > 0 && (
            <DiscoverSection
              icon={Apple}
              iconColor="text-green-600"
              label="Meal Plans"
              count={results.meals.length}
              items={results.meals}
              testIdPrefix="discover-meals"
              renderItems={(visible) => <MealPlansTab mealPlans={visible} />}
            />
          )}

          {/* Creators — limited to 6 initially */}
          {showCreators && results.creators.length > 0 && (
            <DiscoverSection
              icon={Users}
              iconColor="text-purple-600"
              label="Creators"
              count={results.creators.length}
              items={results.creators}
              testIdPrefix="discover-creators"
              renderItems={(visible) => (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {visible.map(c => <CreatorCard key={c.id} creator={c} />)}
                </div>
              )}
            />
          )}

          {/* Challenges */}
          {showChallenges && (results.challenges || []).length > 0 && (
            <DiscoverSection
              icon={Flame}
              iconColor="text-amber-500"
              label="Challenges"
              count={results.challenges.length}
              items={results.challenges}
              testIdPrefix="discover-challenges"
              renderItems={(visible) => (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visible.map(ch => <ChallengeDiscoverCard key={ch.id} challenge={ch} />)}
                </div>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
};
